import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { financeApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, Money } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function PaymentResultPage({ kind }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(kind === "cancel" ? "cancel" : "polling");
  const [detail, setDetail] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await financeApi.status(sessionId);
      setDetail(data);
      if (data.payment_status === "paid") { setState("success"); await refresh(); return true; }
      if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return true; }
    } catch (e) { /* keep polling */ }
    return false;
  }, [sessionId, refresh]);

  useEffect(() => {
    if (kind === "cancel") return;
    let stop = false;
    const run = async () => {
      for (let i = 0; i < 12 && !stop; i++) {
        const done = await poll();
        setAttempts(i + 1);
        if (done) return;
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!stop) setState((s) => (s === "polling" ? "timeout" : s));
    };
    run();
    return () => { stop = true; };
  }, [kind, poll]);

  return (
    <PageContainer className="max-w-lg">
      <Card><CardContent className="flex flex-col items-center gap-4 p-10 text-center">
        {state === "polling" && (<><Loader2 className="h-12 w-12 animate-spin text-secondary" /><h1 className="font-heading text-xl font-semibold">Vérification du paiement…</h1><p className="text-sm text-muted-foreground">Merci de patienter (tentative {attempts}/12).</p></>)}
        {state === "success" && (<>
          <div className="rounded-full bg-emerald-100 p-4"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></div>
          <h1 className="font-heading text-2xl font-bold">Paiement réussi !</h1>
          {detail && (<div className="w-full space-y-1 rounded-xl bg-muted/50 p-4 text-sm">
            <div className="flex justify-between"><span>Montant</span><Money value={detail.base_amount} /></div>
            <div className="flex justify-between text-muted-foreground"><span>Frais de traitement</span><Money value={detail.fee} /></div>
            <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><Money value={detail.amount} className="text-secondary" /></div>
          </div>)}
          <p className="text-sm text-muted-foreground">{detail?.purpose === "collecte" ? "Vous êtes maintenant Membre actif avec accès complet." : "Vos frais d'adhésion sont reçus. Un administrateur validera votre compte."}</p>
          <Button onClick={() => navigate("/tableau-de-bord")} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="paiement-succes-dashboard">Tableau de bord</Button>
        </>)}
        {(state === "cancel" || state === "error" || state === "timeout") && (<>
          <div className="rounded-full bg-rose-100 p-4"><XCircle className="h-10 w-10 text-rose-600" /></div>
          <h1 className="font-heading text-xl font-semibold">{state === "cancel" ? "Paiement annulé" : state === "timeout" ? "Paiement en cours de traitement" : "Paiement non confirmé"}</h1>
          <p className="text-sm text-muted-foreground">{state === "timeout" ? "Le paiement peut prendre quelques instants. Vérifiez votre tableau de bord." : "Aucun montant n'a été prélevé. Vous pouvez réessayer."}</p>
          <div className="flex gap-2"><Button variant="outline" onClick={() => navigate("/cotisations")} data-testid="paiement-reessayer">Retour aux cotisations</Button><Button onClick={() => navigate("/tableau-de-bord")} className="bg-secondary hover:bg-emerald-600 text-white">Tableau de bord</Button></div>
        </>)}
      </CardContent></Card>
    </PageContainer>
  );
}
